import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Undo as RefundIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StoreCustomerSectionCard from './StoreCustomerSectionCard';
import { formatCalendarDate, formatUtcDate } from '../../utils/dateFormatUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { BRAND } from '../../theme/brandTokens';

const TRIGGER_KEYS = {
  CustomerCancellation: 'customerCancellation',
  StoreCancellation: 'storeCancellation',
  ManualAppAdmin: 'manual',
  PaymentAfterCancellation: 'paymentAfterCancellation',
  1: 'manual',
  2: 'customerCancellation',
  3: 'storeCancellation',
  4: 'paymentAfterCancellation',
};

/** Money given back to this customer, newest first, so the store sees who cancels after paying. */
const StoreCustomerRefunds = ({ refunds = [], currencyCode }) => {
  const { t } = useTranslation();

  if (!refunds.length) {
    return null;
  }

  return (
    <StoreCustomerSectionCard
      title={t('customersManagement.details.refunds.title')}
      icon={<RefundIcon fontSize="small" />}
    >
      <Stack spacing={1.25}>
        {refunds.map((refund) => {
          const triggerKey = TRIGGER_KEYS[refund.trigger];
          return (
            <Box
              key={refund.refundId}
              data-testid="customer-refund"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                pb: 1.25,
                '&:not(:last-child)': { borderBottom: '1px solid', borderColor: BRAND.lineSoft },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text }}>
                  {t('customersManagement.details.refunds.appointmentOn', { date: formatCalendarDate(refund.appointmentDate) })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    t('customersManagement.details.refunds.refundedOn', { date: formatUtcDate(refund.refundedAtUtc) }),
                    triggerKey ? t(`customersManagement.details.refunds.triggers.${triggerKey}`) : null,
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0369a1', whiteSpace: 'nowrap' }}>
                {formatCurrency(Number(refund.amount) || 0, refund.currency || currencyCode)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </StoreCustomerSectionCard>
  );
};

export default StoreCustomerRefunds;
